import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject } from 'rxjs';
import { ReferenceService } from '../../../../../shared/reference/reference.service';
import { Logger } from '../../../../../infrastructure/logging/logger';
import { UserContextProvider } from '../../../../../infrastructure/context/user-context-provider';
import { getColor, ColorProp } from '../../../../../layout/Colors';
import { NotificationService } from '../../../../../utilities/PortalNotificationUtility';
import { ClearPrimaryPolicyDialogComponent } from '../../../../policies/components/clear-primary-policy-dialog/clear-primary-policy-dialog.component';
import { DeletePolicyDialogComponent } from '../../../../policies/components/delete-policy-dialog/delete-policy-dialog.component';
import { VerificationPackagesService } from '../../../../verification-packages/services/verification-packages.service';
import { OrganizationService } from '../../../services/organizations.service';
import { OrgAssignVerifPackageDialogComponent } from './components/org-assign-verif-package-dialog/org-assign-verif-package-dialog.component';
import { DataTableComponent } from '../../../../../shared/components/data-table/data-table.component';
import { OrgVerificationPackageColumns, VerificationPackage, getPackageRowFromPackage } from '../../../../verification-packages/models/VerificationPackage';

@Component({
    selector: 'app-org-verification-packages',
    templateUrl: './org-verification-packages.component.html',
    styleUrls: ['./org-verification-packages.component.scss'],
    standalone: true,
    imports: [
      CommonModule,
      DataTableComponent
    ]
})
export class OrgVerificationPackagesComponent implements OnInit {

    @Input() org: any;
    @Output() loadParent: EventEmitter<any> = new EventEmitter();

    dataSource = new MatTableDataSource<any>();
    columns = OrgVerificationPackageColumns;
    loading = new BehaviorSubject<boolean>(false);
    verificationPackages: any[] = [];
    organizationVerificationPackages: VerificationPackage[] = [];
    tableLength = 0;
    pageSize = 10;

    selectedVerificationPackage?: VerificationPackage;
    verificationPackageToUpdate?: VerificationPackage;
    firstVerifPackagePrimary: boolean = false;

    constructor(
        private referenceService: ReferenceService,
        private userContextProvider: UserContextProvider,
        private notificationService: NotificationService,
        private verificationPackagesService: VerificationPackagesService,
        private organizationService: OrganizationService,
        private dialog: MatDialog,
        private logService: Logger
    ) { }

    ngOnInit(): void {
        this.loadPage();
    }

    loadPage() {
        this.loading.next(true);
        this.findVerificationPackages();
        this.findVerificationPackagesByOrgId(this.org.id);
        this.loading.next(false);
    }

    async findVerificationPackages() {
        this.verificationPackages = await this.verificationPackagesService.getVerificationPackages();
    }

    async findVerificationPackagesByOrgId(organizationId: number) {
        this.organizationVerificationPackages = await this.verificationPackagesService.getVerificationPackagesByOrgId(organizationId);
        if (this.organizationVerificationPackages.length > 0) {
            this.firstVerifPackagePrimary = false;
        } else {
            this.firstVerifPackagePrimary = true;
        }
        
        this.populatePackages();
    }

    populatePackages() {
        if (!this.organizationVerificationPackages) {
            this.notificationService.showNotification({ message: `No verification packages found for Organization #${this.org.id}` });
            return;
        }

        const mappedVerificationPackages = this.organizationVerificationPackages.map((verificationPackage: VerificationPackage) => {
            return getPackageRowFromPackage(verificationPackage, this.referenceService, this.userContextProvider.currencyCode);
        });
        this.dataSource = new MatTableDataSource(mappedVerificationPackages);
        this.tableLength = this.dataSource.data.length;
    }

    findVerificationPackageByRow(row: any) {
        let verifPackageSelected = undefined;
        verifPackageSelected = this.verificationPackages.find(verifPackage => verifPackage.id === row.verification_package_id);
        return verifPackageSelected;
    }

    onEditVerificationPackageClick(row: any) {
        this.selectedVerificationPackage = row;
        let verifPackageDetail = this.organizationVerificationPackages.find(orgVerifPackage => orgVerifPackage.verification_package_id === row.verification_package_id);
        
        if (verifPackageDetail) {
            this.onAssignVerificationPackageClick(row, verifPackageDetail);
        }
    }

    onAssignVerificationPackageClick(verificationPackageToUpdate?: any, verificationPackageDetail?: any) {
        let availableVerificationPackages = this.verificationPackages.filter(verifPackage => !this.organizationVerificationPackages.find(orgVerifPackage => verifPackage.id === orgVerifPackage.verification_package_id));
    
        const assignDialogRef = this.dialog.open(OrgAssignVerifPackageDialogComponent, {
            width: '60%',
            maxHeight: '800px',
            disableClose: true,
            autoFocus: false,
            data: {
                organizationId: this.org.id,
                availableVerificationPackages: availableVerificationPackages,
                verificationPackageToUpdate: verificationPackageToUpdate ? verificationPackageToUpdate : undefined,
                verificationPackageDetail: verificationPackageDetail ? verificationPackageDetail : undefined,
                firstVerificationPackageBoolean: this.firstVerifPackagePrimary
            }
        });
        assignDialogRef.afterClosed().subscribe((payload) => {
            if (payload) {
                try {
                    if (this.selectedVerificationPackage) {
                        this.organizationService.updateOrganizationVerificationPackage(this.selectedVerificationPackage.id, payload).then(_ => {
                            this.findVerificationPackagesByOrgId(Number(this.org.id));
                            this.loadParent.emit(this.org.id);
                        });
                    } else {
                        this.organizationService.createOrganizationVerificationPackage(payload).then(_ => {
                            this.findVerificationPackagesByOrgId(Number(this.org.id));
                            this.loadParent.emit(this.org.id);
                        });
                    }
                } catch (e) {
                    this.logService.handleError(e, `An error occurred while submitting Verification Package`, `Error while submitting Verification Package for Organization ID: ${this.org.id}`);
                }
            }
            this.verificationPackageToUpdate = undefined;
        });
    }

    onUpdatePrimaryVerificationPackageClick(row: any) {
        this.selectedVerificationPackage = this.findVerificationPackageByRow(row);
        if (row.primaryStatus === 'Primary') {
            this.openUpdatePrimaryVerificationPackageDialog(row, false);
        } else {
            this.openUpdatePrimaryVerificationPackageDialog(row, true);
        }
    }

    openUpdatePrimaryVerificationPackageDialog(verifPackage: any, setAsPrimaryPackage: boolean) {
        const updateDialogRef = this.dialog.open(ClearPrimaryPolicyDialogComponent, {
            width: '60%',
            disableClose: true,
            data: {
                component: 'Verification Package',
                setAsPrimaryPackage: setAsPrimaryPackage
            }
        });
        updateDialogRef.afterClosed().subscribe((setPrimary: any) => {
            if (setPrimary != 'cancel') {
                let payload = {
                    id: verifPackage.id,
                    verification_package_id: verifPackage.verification_package_id,
                    is_primary: setPrimary,
                    organization_id: verifPackage.organization_id,
                    price: verifPackage.price_number,
                    currency_id: verifPackage.currency_id
                }
                this.organizationService.updateOrganizationVerificationPackage(verifPackage.id, payload).then(_ => {
                    let packagesToRemovePrimary = this.organizationVerificationPackages.filter(vPackage => vPackage.id != verifPackage.id);
                    packagesToRemovePrimary.forEach(verifP => {
                        let payload = {
                            id: verifP.id,
                            verification_package_id: verifP.verification_package_id,
                            is_primary: false,
                            organization_id: verifP.organization_id,
                            price: verifP.price,
                            currency_id: verifP.currency_id
                        }
                        this.organizationService.updateOrganizationVerificationPackage(verifP.id, payload).then(_ => {
                            this.findVerificationPackagesByOrgId(Number(this.org.id));
                        });
                    });
                });
            }
        });
    }

    onDeleteVerificationPackageClick(row: any) {
        this.selectedVerificationPackage = this.findVerificationPackageByRow(row);
        this.openDeleteVerificationPackageDialog(row);
    }

    openDeleteVerificationPackageDialog(verifPackage: any) {
        const deleteDialogRef = this.dialog.open(DeletePolicyDialogComponent, {
            width: '60%',
            disableClose: true,
            data: { component: 'Verification Package' }
        });
        deleteDialogRef.afterClosed().subscribe((data: boolean) => {
            if (data) {
                let payload: any = {
                    id: verifPackage.id,
                    verification_package_id: verifPackage.verification_package_id,
                    is_primary: 0,
                    is_active: 0,
                    organization_id: verifPackage.organization_id
                }
                this.organizationService.updateOrganizationVerificationPackage(verifPackage.id, payload).then(_ => {
                    this.findVerificationPackagesByOrgId(Number(this.org.id));
                });
            }
        });
    }

    getPackagePrimaryColorForTable(primaryflag: string): string {
        if (primaryflag === "Primary") {
            return getColor(ColorProp.PropertyStatusActive);
        } else {
            return '#ED6C23';
        }
    }
}